import React, { useEffect, useState } from "react";
import { Layout, Drawer } from "antd";
import { getClientChannels, getClientReports } from "@ts/retrieve-data";
import { AppDispatch, IChannel, ProductModalType } from "@ts/common-types";
import { useDispatch, useSelector } from "react-redux";
import {
    updateChannel,
    updateChannelDomain,
    updateChannelWalletBalance,
} from "@features/channelSlice";
import { getChannelParams, getClientAttributes } from "@ts/utils";
import {
    setPortfoliosData,
    setSelectedReportData,
} from "@features/gridDataSlice";
import {
    updateActiveClientGroupId,
    updateActiveClientId,
} from "@features/clientDataSlice";
import {
    updateClientTimezone,
    updateEnableBuybox,
    updateEnableDayparting,
    updateEnableKeywordRecommendation,
    updateEnableRevstack,
} from "@features/adminSettingsSlice";
import {
    updateAsinId,
    updateDeepDiveModalDisplay,
    updateProductModalType,
} from "@features/modalSlice";
import { useNavigate } from "react-router-dom";
import ProductAnalysisModule from "../widget-modules/ProductAnalysisModule";

interface IProductAnalyticsDeepDiveModal {
    show: boolean;
}

const ProductAnalyticsDeepDiveModal: React.FC<
    IProductAnalyticsDeepDiveModal
> = ({ show }) => {
    const { Content } = Layout;
    const [loading, setLoading] = useState(true);
    const [selectedSKU, setSelectedSKU] = useState("");
    const channel = useSelector((state: IChannel) => state.channel.value);
    const dispatch = useDispatch<AppDispatch>();
    const navigate = useNavigate();

    const optionsHandler = async (clientId: number, clientGroupId: number) => {
        dispatch(updateActiveClientId(clientId));
        const response = await dispatch(getClientChannels());
        if (response) {
            const channels = response.payload;
            const doNewClientHaveCurrentChannel = channels.some(
                (data: any) => data.channel_id === channel
            );
            if (!doNewClientHaveCurrentChannel) {
                dispatch(updateChannel(channels[0].channel_id));
            }
            const clientCurrentTimezone = getClientAttributes().clientTimezone;
            sessionStorage.removeItem("activeFilters");
            dispatch(setPortfoliosData([]));
            dispatch(updateActiveClientId(clientId));
            dispatch(updateActiveClientGroupId(clientGroupId));
            dispatch(
                updateClientTimezone(
                    clientCurrentTimezone
                        ? clientCurrentTimezone
                        : Intl.DateTimeFormat().resolvedOptions().timeZone
                )
            );
        }

        const clientReportsResponse = await dispatch(getClientReports());
        if (clientReportsResponse) {
            const clientReports = clientReportsResponse.payload;
            if (clientReports.length) {
                dispatch(setSelectedReportData(clientReports[0]));
            }
        }
    };

    // TODO: Can we make this method global as we have similar method in Header.tsx with name onChannelSelection
    const changeChannelhandler = async (value: number) => {
        dispatch(updateChannel(+value));
        await dispatch(getClientChannels());
        const {
            revstack,
            dayparting,
            buybox,
            wallet_balance,
            domain,
            keywordrecommendation,
        } = getChannelParams();
        dispatch(updateEnableDayparting(dayparting));
        dispatch(updateEnableBuybox(buybox));
        dispatch(updateEnableRevstack(revstack));
        dispatch(updateEnableKeywordRecommendation(keywordrecommendation));
        dispatch(updateChannelWalletBalance(wallet_balance));
        dispatch(updateChannelDomain(domain));
    };

    const handleCloseModal = () => {
        navigate(location.pathname, { replace: true });
        dispatch(updateAsinId(""));
        dispatch(updateProductModalType(""));
        dispatch(updateDeepDiveModalDisplay(false));
    };

    useEffect(() => {
        const handleHashChange = () => {
            const hash = window.location.hash;

            if (hash.startsWith("#modal")) {
                const modalHash = hash.split(":");
                const modalData = atob(modalHash[1]).split(":");
                const client = modalData[0];
                const channelArg = modalData[1];
                const identifier = modalData[3];
                const clientGroup = modalData[4];
                setSelectedSKU(identifier);
                optionsHandler(+client, +clientGroup);
                dispatch(
                    updateProductModalType(ProductModalType.PRODUCTS_MODAL)
                );
                dispatch(updateAsinId(identifier));
                changeChannelhandler(+channelArg);
                dispatch(updateDeepDiveModalDisplay(true));
                sessionStorage.setItem(
                    "selectedCards",
                    JSON.stringify(["spend"])
                );
            } else {
                dispatch(updateDeepDiveModalDisplay(false));
            }
        };

        window.addEventListener("hashchange", handleHashChange);

        handleHashChange();

        return () => {
            window.removeEventListener("hashchange", handleHashChange);
        };
    }, []);

    return (
        <Drawer
            title="Product Deep Dive"
            width={"90%"}
            onClose={handleCloseModal}
            open={show}
            style={{ ...(!loading ? { backgroundColor: "#F8F9FA" } : {}) }}
        >
            <Content
                className="overflow-auto py-3 px-1"
                style={{ height: "85vh" }}
            >
                <ProductAnalysisModule
                    {...{
                        setLoading,
                        selectedSKU,
                        setSelectedSKU,
                    }}
                />
            </Content>
        </Drawer>
    );
};

export default ProductAnalyticsDeepDiveModal;
